import React, { type FC, type ReactNode } from 'react';
import {
  MenuItem as MuiMenuItem,
  menuItemClasses,
  type MenuItemProps,
  Stack,
  styled,
  type SxProps,
  type Theme,
  Tooltip,
  Typography
} from '@mui/material';
import { mergeSx } from 'merge-sx';

const MenuItem = styled((props: MenuItemProps) => <MuiMenuItem {...props} />)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1.45, 1.25),
  color: theme.palette.grey[900],

  '&:hover': { backgroundColor: theme.palette.primary[100] },
  [`&.${menuItemClasses.disabled}`]: { opacity: 1, color: theme.palette.text.disabled }
}));

interface DropdownMenuItemProps {
  text?: string;
  onClick?: () => void;
  icon?: ReactNode;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  /** Tooltip text to show on hover (typically used to explain why an item is disabled) */
  tooltip?: string;
}

const MIN_MENU_ROW_HEIGHT = 44;

const DropdownMenuItem: FC<DropdownMenuItemProps> = ({ sx, text, icon, disabled, onClick, tooltip }) => {
  const menuItem = (
    <Stack
      component={MenuItem}
      direction="row"
      alignItems="center"
      onClick={onClick}
      disabled={disabled}
      sx={mergeSx(
        {
          minHeight: MIN_MENU_ROW_HEIGHT,
          p: 1.5,
          cursor: 'pointer',
          gap: 1,
          color: 'grey.900',
          '&:hover': {
            bgcolor: 'primary.100'
          }
        },
        sx,
        disabled && { color: 'grey.500' }
      )}
    >
      {icon && <Stack justifyContent="center">{icon}</Stack>}
      {text && <Typography variant="body2">{text}</Typography>}
    </Stack>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement="left">
        <span>{menuItem}</span>
      </Tooltip>
    );
  }

  return menuItem;
};

export default DropdownMenuItem;
